import { DialogComponent } from '@theme/dialog';

/**
 * Custom Dialog Component
 *
 * DialogComponent를 확장하여 확인/취소 버튼 액션을 처리하는 커스텀 다이얼로그
 * 두 가지 프리셋 지원: 'alert' (단일 버튼), 'confirm' (확인/취소 버튼)
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends DialogComponent
 */
export class CustomDialogComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
  }

  /**
   * 현재 프리셋 타입 반환
   * @returns {'alert' | 'confirm'}
   */
  get preset() {
    /** @type {'alert' | 'confirm'} */
    const preset = /** @type {'alert' | 'confirm'} */ (this.dataset.preset || 'alert');
    return preset;
  }

  /**
   * 확인 버튼 클릭 시 호출되는 메서드
   * on:click="/handleConfirm" 형태로 선언적 바인딩
   */
  handleConfirm = () => {
    const { action, actionData } = this.#getConfirmActionData();

    // 커스텀 이벤트 발생 - 외부에서 추가 처리 가능
    this.dispatchEvent(
      new CustomEvent('custom-dialog:confirm', {
        bubbles: true,
        composed: true,
        detail: { action, data: actionData },
      })
    );

    this.#executeAction(action, actionData);
    this.closeDialog();
  };

  /**
   * 취소 버튼 클릭 시 호출되는 메서드 (confirm 프리셋 전용)
   * on:click="/handleCancel" 형태로 선언적 바인딩
   */
  handleCancel = () => {
    const action = this.dataset.cancelAction || 'close';
    const actionData = this.dataset.cancelActionData;

    // 커스텀 이벤트 발생 - 외부에서 추가 처리 가능
    this.dispatchEvent(
      new CustomEvent('custom-dialog:cancel', {
        bubbles: true,
        composed: true,
        detail: { action, data: actionData },
      })
    );

    this.#executeAction(action, actionData);
    this.closeDialog();
  };

  /**
   * 프리셋에 따른 확인 액션 데이터 반환
   * @returns {{ action: string, actionData: string | undefined }}
   */
  #getConfirmActionData() {
    if (this.preset === 'confirm') {
      return {
        action: this.dataset.confirmAction || 'close',
        actionData: this.dataset.confirmActionData,
      };
    }
    // alert 프리셋 (기본값)
    return {
      action: this.dataset.action || 'close',
      actionData: this.dataset.actionData,
    };
  }

  /**
   * 액션 타입별 처리
   *
   * @param {string | undefined} action - 액션 타입 (redirect, close)
   * @param {string | undefined} data - 액션 데이터 (redirect URL 등)
   */
  #executeAction(action, data) {
    switch (action) {
      case 'redirect':
        if (data) {
          window.location.href = data;
        }
        break;

      case 'close':
        // closeDialog()가 이미 호출되므로 추가 작업 불필요
        break;

      default:
        // 기타 액션은 custom-dialog:confirm/cancel 이벤트 리스너에서 처리
        break;
    }
  }
}

if (!customElements.get('custom-dialog-component')) {
  customElements.define('custom-dialog-component', CustomDialogComponent);
}
